/* HK: optical product preview normalization (non-destructive).
 * Only preview images are adjusted, never full product media galleries.
 * White/transparent source margins are measured in a small offscreen canvas.
 */
(() => {
  'use strict';

  const selector = [
    'product-card .card-gallery img.product-media__image',
    '#MainContent [data-showcase-grid="true"] article > a > img',
    '#MainContent .pp-product-image img'
  ].join(', ');
  const analysisCache = new Map();
  const fitted = new WeakMap();
  const tracked = new WeakSet();
  let scanScheduled = false;

  function getFrame(img) {
    return img.closest('.card-gallery') ||
      img.closest('[data-showcase-grid="true"] article > a') ||
      img.closest('.pp-product-image');
  }

  function analyze(image) {
    if (!image.naturalWidth || !image.naturalHeight) return null;
    const longest = 128;
    const w = Math.max(2, Math.round(longest * image.naturalWidth / Math.max(image.naturalWidth, image.naturalHeight)));
    const h = Math.max(2, Math.round(longest * image.naturalHeight / Math.max(image.naturalWidth, image.naturalHeight)));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    let px;
    try {
      ctx.drawImage(image, 0, 0, w, h);
      px = ctx.getImageData(0, 0, w, h).data;
    } catch (_) {
      // Cross-origin imagery is ignored rather than displaying a broken preview.
      return null;
    }

    const cornerIndexes = [0, w - 1, (h - 1) * w, h * w - 1];
    const corners = cornerIndexes.map(i => {
      const j = i * 4;
      return [px[j], px[j + 1], px[j + 2], px[j + 3]];
    });
    const transparent = corners.filter(c => c[3] < 96).length >= 2;
    const opaqueCorners = corners.filter(c => c[3] >= 96);
    const bg = [0, 1, 2].map(channel =>
      Math.round(opaqueCorners.reduce((sum, c) => sum + c[channel], 0) /
      Math.max(1, opaqueCorners.length))
    );
    if (!transparent && opaqueCorners.some(c =>
      Math.max(Math.abs(c[0] - bg[0]), Math.abs(c[1] - bg[1]), Math.abs(c[2] - bg[2])) > 58
    )) return null;

    const rows = new Uint16Array(h);
    const cols = new Uint16Array(w);
    let painted = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (px[i + 3] < 38) continue;
        let foreground;
        if (transparent) {
          foreground = px[i + 3] > 48;
        } else {
          foreground = Math.max(
            Math.abs(px[i] - bg[0]),
            Math.abs(px[i + 1] - bg[1]),
            Math.abs(px[i + 2] - bg[2])
          ) > 34;
        }
        if (!foreground) continue;
        rows[y]++;
        cols[x]++;
        painted++;
      }
    }

    // Empty pictures or non-uniform photographic backgrounds are left untouched.
    if (painted < w * h * 0.003 || painted > w * h * 0.78) return null;

    const minPerRow = Math.max(1, Math.floor(w * 0.018));
    const minPerColumn = Math.max(1, Math.floor(h * 0.018));
    let left = 0, right = w - 1, top = 0, bottom = h - 1;
    while (left < w - 1 && cols[left] < minPerColumn) left++;
    while (right > left && cols[right] < minPerColumn) right--;
    while (top < h - 1 && rows[top] < minPerRow) top++;
    while (bottom > top && rows[bottom] < minPerRow) bottom--;
    const bw = right - left + 1, bh = bottom - top + 1;
    if (bw < 2 || bh < 2) return null;

    return {
      ratio: image.naturalWidth / image.naturalHeight,
      widthFrac: bw / w,
      heightFrac: bh / h,
      centerX: (left + right + 1) / (2 * w),
      centerY: (top + bottom + 1) / (2 * h)
    };
  }

  function probe(url) {
    if (analysisCache.has(url)) return analysisCache.get(url);
    const result = new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => resolve(analyze(image));
      image.onerror = () => resolve(null);
      image.src = url;
    });
    analysisCache.set(url, result);
    return result;
  }

  function fit(img, data, url) {
    if (!img.isConnected || !data || (img.currentSrc || img.src) !== url) return;
    const frame = getFrame(img);
    if (!frame) return;
    const box = frame.getBoundingClientRect();
    const imgWidth = img.clientWidth;
    const imgHeight = img.clientHeight;
    if (!box.width || !box.height || !imgWidth || !imgHeight) return;

    // object-fit: contain has a letterbox for non-square source images.
    const drawnWidth = Math.min(imgWidth, imgHeight * data.ratio);
    const drawnHeight = Math.min(imgHeight, imgWidth / data.ratio);
    const objectWidth = drawnWidth * data.widthFrac;
    const objectHeight = drawnHeight * data.heightFrac;
    const objectLong = Math.max(objectWidth, objectHeight);
    if (objectLong < 2) return;
    const goal = Math.max(50, Math.min(box.width * 0.78, box.height * 0.79, 205));
    const zoom = Math.max(0.65, Math.min(5.5, goal / objectLong));
    const dx = -(data.centerX - 0.5) * drawnWidth * zoom;
    const dy = -(data.centerY - 0.5) * drawnHeight * zoom;

    img.style.transformOrigin = 'center center';
    img.style.transform = 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px) scale(' + zoom.toFixed(3) + ')';
    img.dataset.hkPreviewNormalized = 'true';
    fitted.set(img, { url, data });
  }

  function normalize(img) {
    if (!img.isConnected || !img.complete || !img.naturalWidth) return;
    const url = img.currentSrc || img.src;
    if (!url) return;
    const existing = fitted.get(img);
    if (existing && existing.url === url) {
      fit(img, existing.data, url);
      return;
    }
    probe(url).then(data => fit(img, data, url)).catch(() => {});
  }

  const observer = ('IntersectionObserver' in window)
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          normalize(entry.target);
        }
      });
    }, { rootMargin: '350px' })
    : null;

  function track(img) {
    if (tracked.has(img)) return;
    tracked.add(img);
    img.addEventListener('load', () => {
      fitted.delete(img);
      normalize(img);
    });
    if (observer) observer.observe(img);
    else normalize(img);
    if (img.complete && img.naturalWidth && !observer) normalize(img);
  }

  function scan() {
    scanScheduled = false;
    document.querySelectorAll(selector).forEach(track);
  }
  function schedule() {
    if (scanScheduled) return;
    scanScheduled = true;
    requestAnimationFrame(scan);
  }

  function start() {
    schedule();
    const nodeObserver = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes' && record.target.matches &&
            record.target.matches(selector)) {
          fitted.delete(record.target);
          normalize(record.target);
          continue;
        }
        if (record.type === 'childList' && [...record.addedNodes].some(node =>
          node.nodeType === 1 && node.matches && (node.matches(selector) || node.querySelector(selector))
        )) {
          schedule();
          break;
        }
      }
    });
    nodeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });
    let resizeScheduled = false;
    window.addEventListener('resize', () => {
      if (resizeScheduled) return;
      resizeScheduled = true;
      requestAnimationFrame(() => {
        resizeScheduled = false;
        document.querySelectorAll(selector).forEach(img => {
          if (fitted.has(img)) normalize(img);
        });
      });
    }, { passive: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
